import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Seo from '../../seo/Seo';
import { FormConsent } from '../../privacy/ConsentUI';
import { RETENTION, DATA_FIDUCIARY } from '../../privacy/privacyConfig';
import { API_BASE_URL } from '../../constants/constants';
import './PrivacyRequest.css';

// The rights a Data Principal has under the DPDP Act 2023 (sections 11–14).
export const REQUEST_TYPES = [
  { key: 'access', label: 'Access', desc: 'A summary of the personal data we hold about you and who we shared it with.' },
  { key: 'correction', label: 'Correction', desc: 'Fix or complete data that is wrong or out of date.' },
  { key: 'erasure', label: 'Erasure', desc: 'Delete data we no longer need, unless the law requires us to keep it.' },
  { key: 'withdraw', label: 'Withdraw consent', desc: 'Stop processing that relies on your consent, such as follow-up emails.' },
  { key: 'nominate', label: 'Nominate someone', desc: 'Name a person to exercise these rights if you die or cannot do so yourself.' },
  { key: 'grievance', label: 'Grievance', desc: 'Complain about how we handled your data or an earlier request.' },
];

const EMPTY = { type: '', name: '', email: '', phone: '', relation: 'self', details: '' };

// DPDP requests travel through the same endpoint as the contact form, with
// a subject line the team filters on.
export const buildRequestPayload = (form) => {
  const type = REQUEST_TYPES.find((t) => t.key === form.type);
  return {
    FullName: form.name.trim(),
    EmailAddress: form.email.trim(),
    PhoneNumber: form.phone.trim(),
    CountryCode: '+91',
    Message: [
      `Subject: DPDP request — ${type ? type.label : form.type}`,
      `Requested by: ${form.relation === 'self' ? 'the Data Principal' : 'a nominee or representative'}`,
      '',
      form.details.trim(),
    ].join('\n'),
  };
};

const PrivacyRequestPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const missing = {
    type: !form.type,
    name: !form.name.trim(),
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()),
    phone: !/^\d{10}$/.test(form.phone.trim()),
    details: !form.details.trim(),
    agreed: !agreed,
  };
  const valid = !Object.values(missing).some(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError('');
    if (!valid) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/PassThroughRayaServices/contactus`, buildRequestPayload(form), {
        headers: { 'X-API-KEY': process.env.REACT_APP_RAYA_SERVICES_API_KEY },
      });
      setDone(true);
      setForm(EMPTY);
      setAgreed(false);
      setSubmitted(false);
    } catch (err) {
      setError(`We could not send this just now. Please email ${DATA_FIDUCIARY.email} with “DPDP request” in the subject.`);
    } finally {
      setSending(false);
    }
  };

  const err = (k) => submitted && missing[k];

  return (
    <div className="pr-page">
      <Seo path="/privacy-request" />
      <div className="pr-container">
        <p className="pr-eyebrow">Digital Personal Data Protection Act, 2023</p>
        <h1>Make a data protection request</h1>
        <p className="pr-intro">
          Use this form to exercise your rights over personal data that {DATA_FIDUCIARY.name} holds as Data
          Fiduciary — for example, the details you gave us on this website or for your Raya account. We
          acknowledge every request within 7 working days and aim to resolve it within 30 days. We may ask you
          to confirm your identity before we act.
        </p>
        <p className="pr-note">
          If your data sits inside a company’s Raya ERP account — as that company’s customer, supplier or
          employee — that company decides how it is used. Please contact them; we will help them answer.
        </p>

        {done && (
          <div className="pr-success" role="status">
            Thank you. Your request has been sent to our privacy team with the subject “DPDP request”. We will
            reply by email within 7 working days.
          </div>
        )}
        {error && <div className="pr-error" role="alert">{error}</div>}

        <form className="pr-form" onSubmit={submit} noValidate>
          <fieldset className={`pr-types ${err('type') ? 'has-error' : ''}`}>
            <legend>What would you like us to do? <span className="pr-req">*</span></legend>
            {REQUEST_TYPES.map((t) => (
              <label key={t.key} className={`pr-type ${form.type === t.key ? 'is-on' : ''}`}>
                <input type="radio" name="type" value={t.key} checked={form.type === t.key} onChange={set('type')} />
                <span><strong>{t.label}</strong><small>{t.desc}</small></span>
              </label>
            ))}
            {err('type') && <span className="pr-field-error">Choose one request type.</span>}
          </fieldset>

          <div className="pr-row">
            <div className="pr-field">
              <label htmlFor="pr-name">Full name <span className="pr-req">*</span></label>
              <input id="pr-name" value={form.name} onChange={set('name')} maxLength={200} aria-invalid={err('name') ? 'true' : undefined} />
              {err('name') && <span className="pr-field-error">Enter your name.</span>}
            </div>
            <div className="pr-field">
              <label htmlFor="pr-email">Email <span className="pr-req">*</span></label>
              <input id="pr-email" type="email" value={form.email} onChange={set('email')} maxLength={200} aria-invalid={err('email') ? 'true' : undefined} />
              {err('email') && <span className="pr-field-error">Enter a valid email — we reply there.</span>}
            </div>
          </div>
          <div className="pr-row">
            <div className="pr-field">
              <label htmlFor="pr-phone">Mobile number <span className="pr-req">*</span></label>
              <input id="pr-phone" type="tel" inputMode="numeric" placeholder="10 digits" value={form.phone} onChange={set('phone')} maxLength={10} aria-invalid={err('phone') ? 'true' : undefined} />
              <span className="pr-hint">Used only to confirm it is really you.</span>
              {err('phone') && <span className="pr-field-error">Enter a 10-digit mobile number.</span>}
            </div>
            <div className="pr-field">
              <label htmlFor="pr-relation">I am making this request</label>
              <select id="pr-relation" value={form.relation} onChange={set('relation')}>
                <option value="self">for myself</option>
                <option value="nominee">as a nominee or on someone’s behalf</option>
              </select>
            </div>
          </div>
          <div className="pr-field">
            <label htmlFor="pr-details">Details <span className="pr-req">*</span></label>
            <textarea id="pr-details" rows={5} maxLength={2000} value={form.details} onChange={set('details')} placeholder="Tell us what the request is about, e.g. which email you used with us." aria-invalid={err('details') ? 'true' : undefined} />
            {err('details') && <span className="pr-field-error">Tell us a little about the request.</span>}
          </div>

          <FormConsent
            id="pr-consent"
            checked={agreed}
            onChange={setAgreed}
            error={err('agreed')}
            purpose="We use these details only to verify you, act on this request and keep a record of how we handled it."
            data="your name, email, mobile number and what you tell us here"
            retention={RETENTION.privacyRequest}
          />

          <button type="submit" className="pr-submit" disabled={sending}>{sending ? 'Sending…' : 'Send request'}</button>
        </form>

        <div className="pr-alt">
          <h2>Other ways to reach us</h2>
          <p>
            Email <a href={`mailto:${DATA_FIDUCIARY.email}?subject=DPDP%20request`}>{DATA_FIDUCIARY.email}</a> with
            “DPDP request” in the subject, or write to {DATA_FIDUCIARY.name}, {DATA_FIDUCIARY.address}.
          </p>
          <p>
            For a grievance, use “Privacy grievance” as the subject. If we do not resolve it, you can complain
            to the Data Protection Board of India. See the{' '}
            <Link to="/privacy-policy#rights">privacy notice</Link> for details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyRequestPage;
